import React, { FormEventHandler, useEffect } from 'react';
import {
  reduxForm,
  Field,
  formValueSelector,
  getFormValues,
  getFormSyncErrors,
  initialize,
} from 'redux-form/immutable';
import { useDispatch, useSelector } from 'react-redux';
import LinearProgress from '@material-ui/core/LinearProgress';
import { Button, Grid, Typography } from '@material-ui/core';
import type { InjectedFormProps } from 'redux-form';
import _ from 'lodash';

import { selectUser, selectOrganization } from 'entities/user/selectors';
import Box from 'retired/Box';
import FormTextField from 'retired/FormTextField';
import { required, isURL, isHexColor } from 'utils/validators';
import type { State } from 'reducers';
import type { TypedMap } from 'utils/typed-map';
import { showErrorAction } from 'containers/Notification/actions';
import { OrganizationLabel } from 'components/OrganizationLabel/OrganizationLabel';
import FormDescriptionRteField from 'retired/FormDescriptionRteField';
import { ViewInAppButton } from 'components/ViewInAppButton';
import FormFieldLabel from 'retired/FormFieldLabel';
import FormFileUpload from 'retired/FormFileUpload';
import { getAppDomain } from 'utils/getAppDomain';
import { imageTypes } from 'retired/FileUpload/utils';
import { NonprofitEntityType } from 'entities/organization/model';

import { RequestNonprofitNameChange } from './RequestNonprofitNameChange';
import * as Actions from './actions';

const requiredValidator = required();
const isUrlValidator = isURL();
const isHexColorValidator = isHexColor();

const FORM_IDENTIFIER = 'app/OrganizationPage/Brand';

type FormData = {
  name: string;
  brandColor: string;
  legalName: string;
  logo: string;
  mainPicture: string;
  emailSenderName: string;
  emailSignature: {
    main: string;
  };
  website: string;
};

export const valueSelector: <K extends keyof FormData>(state: State, key: K) => FormData[K] =
  formValueSelector(FORM_IDENTIFIER);

const Brand = ({ submitting, initialized }: InjectedFormProps) => {
  const dispatch = useDispatch();
  const organization = useSelector((state: State) => selectOrganization(state, true));
  const user = useSelector(selectUser);
  const syncErrors = useSelector(getFormSyncErrors(FORM_IDENTIFIER));
  const brandColor = useSelector((state: State) => valueSelector(state, 'brandColor'));
  const registeredFields = useSelector((state: State) =>
    state.getIn(['form', FORM_IDENTIFIER, 'registeredFields'])
  );

  const isAdmin = user?.isAdmin;

  useEffect(() => {
    dispatch(initialize(FORM_IDENTIFIER, organization?.toJS() || {}));
  }, []);

  const currentFormValues = useSelector(getFormValues(FORM_IDENTIFIER)) as TypedMap<FormData>;

  if (!organization || !initialized) {
    return <LinearProgress />;
  }

  const submit = (data = currentFormValues.toJS()) => {
    const firstSyncError = Object.values(syncErrors)[0];

    if (firstSyncError) {
      dispatch(showErrorAction(firstSyncError));
    } else {
      const formKeys = registeredFields ? [...registeredFields?.keys()] : [];
      dispatch(Actions.saveAction(_.pick(data, ['id', ...formKeys]), FORM_IDENTIFIER));
    }
  };

  const isCompany = organization.isCompany();
  const isGovernmentEntity =
    organization.nonprofitEntityType === NonprofitEntityType.governmentEntity;

  const handleFormEvent: FormEventHandler = (event) => {
    event.preventDefault();
    submit();
  };

  return (
    // eslint-disable-next-line react/no-unknown-property
    <form noValidate onSubmitCapture={handleFormEvent}>
      <fieldset>
        <Box style={{ paddingTop: 0, marginBottom: 30 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
            }}
          >
            <h2>Your Brand</h2>
            {isAdmin && (
              <div
                style={{
                  marginRight: 30,
                  marginTop: 25,
                  textAlign: 'right',
                  flexGrow: 1,
                }}
              >
                Organization ID: {organization.id}
              </div>
            )}
            {!isCompany && (
              <ViewInAppButton
                href={`${getAppDomain([organization, user])}/organization/${organization.id}`}
                iconSize={18}
                style={{
                  display: 'inline-block',
                  marginRight: 30,
                  marginTop: 25,
                }}
              />
            )}
          </div>
          <div style={{ marginTop: -8, marginBottom: 8 }}>
            <OrganizationLabel organization={organization} />
          </div>
          <Grid container spacing={3}>
            <Grid item sm={12} lg={6}>
              <Field
                id="name"
                name="name"
                component={FormTextField}
                inputProps={{ maxLength: 100 }}
                fullWidth
                autoFocus
                required
                label={isCompany ? 'Company Name' : 'Nonprofit Name'}
                validate={requiredValidator}
                alwaysDisplayErrors
                disabled={!user?.isAdmin && !isCompany}
              />

              <RequestNonprofitNameChange />

              {isGovernmentEntity && (
                <>
                  <Field
                    id="legalName"
                    name="legalName"
                    component={FormTextField}
                    inputProps={{ maxLength: 100 }}
                    fullWidth
                    autoFocus
                    label="Legal name"
                    alwaysDisplayErrors
                    disabled={!user?.isAdmin}
                  />

                  <br />
                </>
              )}

              {isCompany && (
                <>
                  <br />
                  <br />
                  <div style={{ position: 'relative' }}>
                    <div
                      style={{
                        width: '20px',
                        height: '20px',
                        backgroundColor: brandColor,
                        borderRadius: '3px',
                        border: '1px solid rgba(0, 0, 0, .5)',
                        position: 'absolute',
                        bottom: 25,
                        right: 0,
                      }}
                    />
                    <Field
                      id="brandColor"
                      name="brandColor"
                      component={FormTextField}
                      inputProps={{ maxLength: 7 }}
                      fullWidth
                      label="Brand Color"
                      helperText="Please use a hex code (e.g. #FFFFFF)"
                      validate={isHexColorValidator}
                    />
                  </div>
                </>
              )}
              <br />
              <Field
                id="website"
                name="website"
                component={FormTextField}
                fullWidth
                label="Website"
                helperText="Website URL"
                validate={isUrlValidator}
              />
              <br />
              <br />
              <Field
                name="emailSenderName"
                component={FormTextField}
                fullWidth
                inputProps={{ maxLength: 40 }}
                label="Email Sender Name"
                helperText="Set the sender name ('From Name') for your automated emails. This is the name displayed in the inbox, next to the subject line. The default is 'The Deed Team'."
              />
              <br />
              <br />
              <Field
                name="emailSignature.main"
                component={FormDescriptionRteField}
                fullWidth
                multiline
                label="Email Signature"
                helperText="Displayed at the bottom of emails"
              />
            </Grid>
            <Grid item sm={12} lg={6}>
              <FormFieldLabel>Logo</FormFieldLabel>
              <Field
                id="mainPicture"
                name="mainPicture"
                component={FormFileUpload}
                labelText="Upload an image of your organization's logo. Make sure the image is transparent and in PNG format."
                acceptedTypes="image/png"
                maxSize={1024 * 1024 * 10}
                imagePreview
                withCrop
              />
              <br />
              <FormFieldLabel>Compact Logo (round)</FormFieldLabel>
              <Field
                id="logo"
                name="logo"
                component={FormFileUpload}
                labelText="Upload an image of your organization's logo to fit our secondary format. The image can be in either PNG or JPG format."
                acceptedTypes={imageTypes}
                maxSize={1024 * 1024 * 10}
                cropRatio={1}
                fixed
                imagePreview
                withCrop
                allowClear
                round
                isZoomable
              />
            </Grid>
          </Grid>
          <Typography align="right" style={{ marginTop: 30 }}>
            <Button type="submit" variant="contained" color="primary" disabled={submitting}>
              Submit
            </Button>
          </Typography>
        </Box>
      </fieldset>
    </form>
  );
};

const withForm = reduxForm({
  form: FORM_IDENTIFIER,
  destroyOnUnmount: false,
  enableReinitialize: true,
  keepDirtyOnReinitialize: true,
});

export default withForm(Brand);
